import { PropsWithChildren, useEffect } from "react";
import { atom, useRecoilState } from "recoil";

interface NoFramingSandbox {
    isNoFramingSandbox: boolean;
    container: HTMLElement | null;
}

/**
 * Atom that tracks whether the current environment is a NoFraming sandbox
 * This flag is set when the component is rendered through shadow DOM
 */
const noFramingSandboxAtom = atom<NoFramingSandbox>({
    key: "no-framing-sandbox",
    default: {
        isNoFramingSandbox: false,
        container: null,
    },
});

export const useNoFramingSandbox = () => {
    const [noFramingSandbox, setNoFramingSandbox] =
        useRecoilState(noFramingSandboxAtom);

    return {
        ...noFramingSandbox,
        setNoFramingSandbox,
    };
};

interface NoFramingSandboxProviderProps {
    container: NoFramingSandbox["container"];
}

/**
 * Provider that sets the NoFraming sandbox flag when the component is
 * rendered through shadow DOM
 */
export const NoFramingSandboxProvider: React.FC<
    PropsWithChildren<NoFramingSandboxProviderProps>
> = ({ children, container }) => {
    const { isNoFramingSandbox, setNoFramingSandbox } = useNoFramingSandbox();

    useEffect(() => {
        setNoFramingSandbox({
            isNoFramingSandbox: true,
            container,
        });
    }, []);

    return isNoFramingSandbox ? <>{children}</> : null;
};
